import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { surveilleSociete } from '../lib/societe-ouverte';
import { surSessionPerdue } from './client';
import { authApi } from './resources';

/**
 * Session de l'utilisateur connecté.
 *
 * <p>L'authentification est établie côté serveur : le navigateur ne détient
 * qu'un cookie qu'il ne peut pas lire. L'interface n'a donc aucun jeton à
 * conserver — elle demande au démarrage à qui elle a affaire, et le serveur
 * répond ou refuse.
 */
const SessionContext = createContext({
  session: null,
  chargement: true,
  connexion: async () => {},
  connexionCode: async () => {},
  connexionSecours: async () => {},
  ouvreLien: async () => {},
  connexionPin: async () => {},
  inscription: async () => {},
  deconnexion: async () => {},
  adopteCompte: () => {},
});

/** Correspondance entre les rôles du serveur et ceux de l'interface. */
const ROLES = {
  PATRON: 'patron',
  TECHNICIEN: 'technicien',
  // Directeur des travaux : au-dessus des techniciens, en dessous du patron.
  DIRECTEUR: 'directeur',
  COMPTABLE: 'comptable',
  SECRETAIRE: 'secretaire',
  OUVRIER: 'ouvrier',
  // Le client du magasin : il n'ouvre que son espace client.
  CLIENT: 'client',
};

/**
 * Traduit un compte de l'API en session d'interface.
 *
 * @param {object} compte compte renvoyé par l'API
 * @returns {object} la session correspondante
 */
export function dtoVersSession(compte) {
  return {
    id: compte.id,
    email: compte.email,
    nom: compte.nomComplet,
    role: ROLES[compte.role] ?? 'ouvrier',
    entrepriseId: compte.companyId,
    entrepriseNom: compte.entrepriseNom ?? null,
    employeId: compte.employeeId ?? null,
    // L'ancien compte technique du code PIN n'a ni vraie adresse ni mot de
    // passe connu : il n'a rien à changer, et doit être réinvité.
    accesParPin: compte.accesParPin === true,
  };
}

/**
 * Fournit la session à l'arbre de composants.
 *
 * @param {object} props propriétés du composant
 * @param {import('react').ReactNode} props.children arbre à envelopper
 * @returns {import('react').ReactElement} le fournisseur de contexte
 */
export function SessionProvider({ children }) {
  const [session, setSession] = useState(null);
  const [chargement, setChargement] = useState(true);

  // Au démarrage, l'interface ignore si un cookie de session valable existe
  // encore : elle le demande au serveur, seul juge en la matière.
  useEffect(() => {
    let annule = false;
    authApi
      .moi()
      .then((compte) => {
        if (!annule && compte) setSession(dtoVersSession(compte));
      })
      .catch(() => {
        // Absence de session : c'est le cas normal au premier lancement.
      })
      .finally(() => {
        if (!annule) setChargement(false);
      });
    return () => {
      annule = true;
    };
  }, []);

  // Une session peut expirer pendant l'usage : le serveur le signale par un
  // refus, d'où qu'il vienne, et l'interface revient à l'écran de connexion.
  useEffect(() => surSessionPerdue(() => setSession(null)), []);
  // Un autre onglet ouvre l'autre société du Mix : celui-ci se recharge
  // plutôt que de travailler dans une société qu'il n'affiche pas.
  const societeOuverte = session?.entrepriseId ?? null;
  useEffect(() => surveilleSociete(societeOuverte), [societeOuverte]);

  const connexion = useCallback(async (email, motDePasse) => {
    const reponse = await authApi.connexion(email, motDePasse);
    // Le mot de passe ne suffit pas : le serveur attend le code à six
    // chiffres, reçu par e-mail ou par SMS, et rend le défi que l'écran
    // présentera avec le choix du moyen.
    if (reponse?.codeRequis) return reponse;
    setSession(dtoVersSession(reponse));
    return reponse;
  }, []);

  const connexionCode = useCallback(async (defi, code) => {
    const compte = await authApi.connexionCode(defi, code);
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  // Téléphone perdu, e-mail éteint : un code de secours tient lieu de code.
  const connexionSecours = useCallback(async (defi, code) => {
    const compte = await authApi.connexionSecours(defi, code);
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  // Le lien remplace le mot de passe, pas le code : quand le serveur rend
  // l'état « code requis », l'écran demande le code avant toute session.
  const ouvreLien = useCallback(async (jeton) => {
    const reponse = await authApi.ouvreLien(jeton);
    if (reponse?.codeRequis) return reponse;
    setSession(dtoVersSession(reponse));
    return reponse;
  }, []);

  const connexionPin = useCallback(async (entrepriseId, employeId, pin) => {
    const compte = await authApi.connexionPin(entrepriseId, employeId, pin);
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  const inscription = useCallback(async (demande) => {
    await authApi.inscription(demande);
    // L'inscription ne connecte pas : elle crée le compte, la connexion suit,
    // ce qui évite de traiter deux chemins d'ouverture de session.
    const compte = await authApi.connexion(demande.email, demande.motDePasse);
    // Comme à chaque connexion, le code à six chiffres d'abord : l'écran le
    // demande, puis la session s'ouvre.
    if (compte?.codeRequis) return compte;
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  // Basculer ou creer une societe reemet la session cote serveur : la page
  // entiere est rechargee ensuite, parce que chaque collection en memoire
  // appartient a la societe qu'on quitte. Recharger est la seule facon sure
  // de n'en garder aucune trace.
  const basculeSociete = useCallback(async (id) => {
    const compte = await authApi.basculeSociete(id);
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  const creeSociete = useCallback(async (demande) => {
    const compte = await authApi.creeSociete(demande);
    setSession(dtoVersSession(compte));
    return compte;
  }, []);

  // Le serveur reemet parfois la session sans changer de societe : le patron
  // qui se rattache a sa fiche d'employe en revient avec elle. L'interface
  // adopte le compte tel qu'il le rend, sans se reconnecter.
  const adopteCompte = useCallback((compte) => {
    if (compte) setSession(dtoVersSession(compte));
  }, []);

  const deconnexion = useCallback(async () => {
    try {
      await authApi.deconnexion();
    } catch {
      // L'échec de la requête ne change rien à la suite : la session locale est
      // fermée dans tous les cas. Laisser l'écran ouvert après une demande de
      // déconnexion serait pire qu'une erreur réseau, et le serveur écartera de
      // lui-même une session qu'il ne reconnaît plus.
    }
    setSession(null);
  }, []);

  const valeur = useMemo(
    () => ({
      session,
      chargement,
      connexion,
      connexionCode,
      connexionSecours,
      ouvreLien,
      connexionPin,
      inscription,
      deconnexion,
      basculeSociete,
      creeSociete,
      adopteCompte,
    }),
    [
      session,
      chargement,
      connexion,
      connexionCode,
      connexionSecours,
      ouvreLien,
      connexionPin,
      inscription,
      deconnexion,
      basculeSociete,
      creeSociete,
      adopteCompte,
    ],
  );

  return <SessionContext.Provider value={valeur}>{children}</SessionContext.Provider>;
}

/**
 * Accède à la session en cours.
 *
 * @returns {{session: object|null, chargement: boolean, connexion: Function,
 *            connexionCode: Function, connexionPin: Function, inscription: Function,
 *            deconnexion: Function}}
 *          la session et ses commandes
 */
export function useSession() {
  return useContext(SessionContext);
}
